import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Shield, ShieldCheck, Smartphone, Key,
  Copy, Eye, EyeOff, X, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useEmbeddedMode, postEmbeddedStepComplete } from '../hooks/useEmbeddedMode';
import { usePageHeader } from '../context/PageHeaderContext';

const MFAPage = () => {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const embedded = useEmbeddedMode();
  const [mfaStatus, setMfaStatus] = useState({
    mfa_enabled: false,
    totp_enabled: false,
    recovery_key_enabled: false
  });
  const [loading, setLoading] = useState(true);

  // Hoist title + back arrow into the global app header (audit pass).
  usePageHeader({
    title: 'Two-Factor Authentication',
    back: () => navigate('/'),
  }, []);

  // Modal states
  const [showAuthenticatorModal, setShowAuthenticatorModal] = useState(false);
  const [showRecoveryKeyModal, setShowRecoveryKeyModal] = useState(false);
  
  // Authenticator App states
  const [totpSecret, setTotpSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingTotp, setVerifyingTotp] = useState(false);

  // Recovery Key states
  const [recoveryKeyStep, setRecoveryKeyStep] = useState('initial'); // initial, password, reveal, display
  const [password, setPassword] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState('');
  const [showRecoveryKey, setShowRecoveryKey] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);

  useEffect(() => {
    fetchMfaStatus();
  }, []);

  const fetchMfaStatus = async () => {
    try {
      const response = await api.get('/mfa/status');
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============ Authenticator App Functions ============
  const handleSetupAuthenticator = async () => {
    setShowAuthenticatorModal(true);
    try {
      const response = await api.post('/mfa/setup-totp');
      setTotpSecret(response.data.totp_secret);
      setQrCode(response.data.qr_code);
    } catch (error) {
      toast.error('Failed to setup authenticator');
      setShowAuthenticatorModal(false);
    }
  };

  const handleVerifyTotp = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifyingTotp(true);
    try {
      await api.post('/mfa/verify-totp', { code: verificationCode });
      toast.success('Two-factor authentication enabled!');
      await refreshUser();
      await fetchMfaStatus();
      setShowAuthenticatorModal(false);
      setVerificationCode('');
      if (embedded) postEmbeddedStepComplete('step2d_mfa_activated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingTotp(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // ============ Recovery Key Functions ============
  const handleOpenRecoveryKey = () => {
    setShowRecoveryKeyModal(true);
    setRecoveryKeyStep(mfaStatus.recovery_key_enabled ? 'initial' : 'initial');
    setPassword('');
    setRecoveryKey('');
    setShowRecoveryKey(false);
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setVerifyingPassword(true);
    try {
      await api.post('/mfa/verify-password', { password });
      setRecoveryKeyStep('reveal');
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleGenerateRecoveryKey = async () => {
    setGeneratingKey(true);
    try {
      const endpoint = mfaStatus.recovery_key_enabled 
        ? '/mfa/regenerate-recovery-key' 
        : '/mfa/generate-recovery-key';
      const response = await api.post(endpoint);
      setRecoveryKey(response.data.recovery_key);
      setRecoveryKeyStep('display');
      await refreshUser();
      await fetchMfaStatus();
    } catch (error) {
      toast.error('Failed to generate recovery key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const closeRecoveryKeyModal = () => {
    setShowRecoveryKeyModal(false);
    setRecoveryKeyStep('initial');
    setPassword('');
    setRecoveryKey('');
    setShowRecoveryKey(false);
  };

  // iter166 — Modal JSX hoisted out of the standalone return so the
  // embedded view can render the same Authenticator + Recovery Key
  // dialogs. JSX held in consts (not as sub-components) keeps focus
  // inside the inputs across re-renders.
  const authenticatorModal = (
    <Dialog open={showAuthenticatorModal} onOpenChange={setShowAuthenticatorModal}>
      <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh]">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="text-lg font-bold">Setup Authenticator App</DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Copy and paste the secret key or scan the QR code
          </p>
        </DialogHeader>

        <div className="px-4 pb-4 space-y-3 overflow-y-auto max-h-[60vh]">
          <div className="bg-[#f5f5f5] rounded-xl p-3">
            <Label className="text-xs text-[#676767]">
              Enter this secret key in your authenticator app:
            </Label>
            <div className="mt-1.5 bg-white rounded-lg p-2 text-center overflow-x-auto">
              <code className="text-xs font-mono tracking-wide text-[#1c1a1c] break-all">
                {totpSecret}
              </code>
            </div>
            <Button
              variant="outline"
              className="w-full mt-2 h-9 rounded-xl text-sm"
              onClick={() => copyToClipboard(totpSecret, 'Secret key')}
              data-testid="copy-secret-key-btn"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Copy Key to Clipboard
            </Button>

            <button
              onClick={() => setShowQrCode(!showQrCode)}
              className="w-full text-center mt-2 text-xs text-[#757150] hover:underline"
            >
              {showQrCode ? 'Hide QR Code' : 'See QR Code'}
            </button>

            {showQrCode && qrCode && (
              <div className="mt-2 flex justify-center">
                <img src={qrCode} alt="QR Code" className="w-28 h-28 rounded-lg" />
              </div>
            )}
          </div>

          <div className="bg-[#f5f5f5] rounded-xl p-3">
            <Label className="text-xs text-[#676767]">
              Enter the 6-digit code from your app:
            </Label>
            <Input
              type="text"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="mt-1.5 text-center text-lg tracking-widest h-10 rounded-xl"
              style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}
              maxLength={6}
              data-testid="totp-code-input"
            />
          </div>

          <Button
            className="w-full bg-[#757150] hover:bg-[#5c5840] text-white h-10 rounded-xl"
            onClick={handleVerifyTotp}
            disabled={verifyingTotp || verificationCode.length !== 6}
            data-testid="enable-totp-btn"
          >
            {verifyingTotp ? 'Verifying...' : 'Enable Two-Factor Authentication'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const recoveryKeyModal = (
    <Dialog open={showRecoveryKeyModal} onOpenChange={closeRecoveryKeyModal}>
      <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh]">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="text-lg font-bold">
            {mfaStatus.recovery_key_enabled ? 'Regenerate Recovery Key' : 'Setup Recovery Key'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {recoveryKeyStep === 'initial' && 'Click below to begin the process'}
            {recoveryKeyStep === 'password' && 'Enter your password to continue'}
            {recoveryKeyStep === 'reveal' && 'Your recovery key is ready to be revealed'}
            {recoveryKeyStep === 'display' && 'Your recovery key is ready to be revealed'}
          </p>
        </DialogHeader>

        <div className="px-4 pb-4 space-y-3 overflow-y-auto max-h-[60vh]">
          {recoveryKeyStep === 'initial' && (
            <div className="bg-[#f5f5f5] rounded-xl p-3">
              <p className="text-xs text-[#1c1a1c] mb-1.5">
                Click the button below to activate your unique Recovery Key. <span className="font-semibold">NOTE:</span>
              </p>
              <ul className="text-xs text-[#676767] space-y-0.5">
                <li>• Store it in a secure location!</li>
                <li>• Recovery Keys cannot be disabled but may be regenerated if seen, stolen, or lost.</li>
              </ul>

              <Button
                className="w-full mt-3 bg-[#757150] hover:bg-[#5c5840] text-white h-10 rounded-xl"
                onClick={() => setRecoveryKeyStep('password')}
                data-testid="activate-recovery-key-btn"
              >
                <Key className="w-4 h-4 mr-2" />
                Activate Recovery Key
              </Button>
            </div>
          )}

          {recoveryKeyStep === 'password' && (
            <div className="bg-[#f5f5f5] rounded-xl p-3">
              <Label className="text-xs text-[#676767]">
                Enter your password to continue:
              </Label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-9 rounded-xl"
                data-testid="recovery-password-input"
              />

              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  className="flex-1 h-9 rounded-xl"
                  onClick={() => setRecoveryKeyStep('initial')}
                >
                  Back
                </Button>
                <Button
                  className="flex-1 h-9 rounded-xl bg-[#757150] hover:bg-[#5c5840] text-white"
                  onClick={handleVerifyPassword}
                  disabled={verifyingPassword || !password}
                  data-testid="verify-password-btn"
                >
                  {verifyingPassword ? 'Verifying...' : 'Verify Password'}
                </Button>
              </div>
            </div>
          )}

          {recoveryKeyStep === 'reveal' && (
            <div className="bg-[#f5f5f5] rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="font-semibold text-sm text-[#1c1a1c]">
                  Your password has been verified.
                </p>
              </div>
              <p className="text-xs text-[#676767] mb-3">
                Click below to reveal your Recovery Key.
              </p>

              <Button
                className="w-full bg-[#757150] hover:bg-[#5c5840] text-white h-10 rounded-xl"
                onClick={handleGenerateRecoveryKey}
                disabled={generatingKey}
                data-testid="reveal-recovery-key-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                {generatingKey ? 'Generating...' : 'Reveal Recovery Key'}
              </Button>
            </div>
          )}

          {recoveryKeyStep === 'display' && (
            <div className="bg-[#f5f5f5] rounded-xl p-3">
              <Label className="text-xs text-[#676767]">
                Your 12-digit Recovery Key:
              </Label>
              <div className="mt-1.5 bg-white rounded-lg p-2 text-center relative">
                {showRecoveryKey ? (
                  <code className="text-base font-mono tracking-wider text-[#1c1a1c]">
                    {recoveryKey}
                  </code>
                ) : (
                  <code className="text-base font-mono tracking-wider text-[#1c1a1c] blur-sm select-none">
                    {recoveryKey}
                  </code>
                )}
              </div>

              <div className="flex items-start gap-2 mt-2 p-2 bg-yellow-50 rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-yellow-800">
                  Store this code in a secure location. You'll need it to access your account if you lose access to your other 2FA methods.
                </p>
              </div>

              <Button
                variant="outline"
                className="w-full mt-2 h-9 rounded-xl"
                onClick={() => setShowRecoveryKey(!showRecoveryKey)}
                data-testid="toggle-recovery-key-visibility"
              >
                {showRecoveryKey ? (
                  <>
                    <EyeOff className="w-3.5 h-3.5 mr-1.5" />
                    Hide Code
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                    Show Code
                  </>
                )}
              </Button>

              <Button
                className="w-full mt-2 bg-[#757150] hover:bg-[#5c5840] text-white h-9 rounded-xl"
                onClick={() => copyToClipboard(recoveryKey, 'Recovery key')}
                data-testid="copy-recovery-key-btn"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copy Key to Clipboard
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className={embedded ? "min-h-screen bg-white flex items-center justify-center" : "min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] flex items-center justify-center"}>
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 bg-primary/20 rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading MFA settings...</p>
        </div>
      </div>
    );
  }

  // iter166 — Embedded popup layout (matches the NDA popup styling).
  // Force light mode, hero on top, content card in the middle, the
  // two action rows feel like olive-accent callouts. The Authenticator
  // and Recovery Key modals (rendered below) appear on top.
  if (embedded) {
    const bothDone = mfaStatus.totp_enabled && mfaStatus.recovery_key_enabled;
    return (
      <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
        {/* Hero */}
        <div className="px-6 pt-8 pb-4 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
            {bothDone ? (
              <ShieldCheck className="w-8 h-8 text-[#757150]" />
            ) : (
              <Shield className="w-8 h-8 text-[#757150]" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-[#1c1a1c]">Activate MFA</h2>
        </div>

        {/* Body */}
        <div className="px-6 flex-1 flex flex-col justify-center">
          <div className="rounded-2xl bg-[#fafaf7] border border-[#ece9df] p-5 shadow-sm">
            <p className="text-[14.5px] text-[#3a3a3a] leading-[1.6]">
              Two-factor authentication adds a critical second layer of security to your account.
              Even if your password is compromised, your account stays protected. We require{' '}
              <span className="font-semibold text-[#1c1a1c]">both methods below</span> to be enabled
              before continuing onboarding.
            </p>

            <div className="my-4 h-px bg-[#ece9df]" />

            {/* Authenticator App row */}
            <button
              type="button"
              onClick={handleSetupAuthenticator}
              disabled={mfaStatus.totp_enabled}
              className="w-full text-left rounded-xl bg-white border border-[#ece9df] p-3 flex items-center gap-3 hover:border-[#c9c4a8] disabled:opacity-70 disabled:cursor-default transition-colors mb-2"
              data-testid="authenticator-app-option"
            >
              <div className="w-10 h-10 rounded-full bg-[#f5f5f0] flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-4 h-4 text-[#757150]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-[#1c1a1c]">Authenticator App</p>
                  {mfaStatus.totp_enabled && (
                    <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                      Enabled
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-[#676767] leading-snug truncate">
                  Google Authenticator, Authy, or Microsoft Authenticator
                </p>
              </div>
              {mfaStatus.totp_enabled ? (
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              ) : (
                <span className="text-xs font-semibold text-[#757150] flex-shrink-0">Set up →</span>
              )}
            </button>

            {/* Recovery Key row */}
            <button
              type="button"
              onClick={handleOpenRecoveryKey}
              className="w-full text-left rounded-xl bg-white border border-[#ece9df] p-3 flex items-center gap-3 hover:border-[#c9c4a8] transition-colors"
              data-testid="recovery-key-option"
            >
              <div className="w-10 h-10 rounded-full bg-[#f5f5f0] flex items-center justify-center flex-shrink-0">
                <Key className="w-4 h-4 text-[#757150]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-[#1c1a1c]">Recovery Key</p>
                  {mfaStatus.recovery_key_enabled && (
                    <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                      Enabled
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-[#676767] leading-snug truncate">
                  A 12-digit backup if you lose your authenticator
                </p>
              </div>
              {mfaStatus.recovery_key_enabled ? (
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              ) : (
                <span className="text-xs font-semibold text-[#757150] flex-shrink-0">Set up →</span>
              )}
            </button>
          </div>

          {/* Olive-accent reassurance callout */}
          <div className="mt-4 rounded-2xl bg-[#f5f5f0] border-l-[3px] border-[#757150] px-4 py-3.5 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <ShieldCheck className="w-4 h-4 text-[#757150]" />
            </div>
            <div className="flex-1">
              <p className="text-[13.5px] font-semibold text-[#1c1a1c] mb-0.5">Why both?</p>
              <p className="text-[12.5px] text-[#5a5a5a] leading-[1.5]">
                The authenticator handles daily logins. The recovery key is your fallback if you
                ever lose your phone — store it somewhere safe.
              </p>
            </div>
          </div>
        </div>

        {/* Footer — anchored continue / status button */}
        <div
          className="px-6 pt-4 pb-6 bg-white"
          style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        >
          <Button
            className="w-full h-12 rounded-xl flex items-center justify-center gap-2 text-base shadow-sm bg-[#757150] hover:bg-[#5c5840] text-white disabled:opacity-60"
            disabled={!bothDone}
            onClick={() => {
              if (bothDone) postEmbeddedStepComplete('step2d_mfa_activated');
            }}
            data-testid="mfa-continue-btn"
          >
            <ShieldCheck className="w-4 h-4" />
            {bothDone ? 'Continue' : 'Enable both methods to continue'}
          </Button>
        </div>

        {/* Authenticator + Recovery Key modals (unchanged — rendered as a portal sibling) */}
        {authenticatorModal}
        {recoveryKeyModal}
      </div>
    );
  }

  return (
    <div className={embedded ? "p-2" : "min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24"}>
      <div className="max-w-md mx-auto pt-6">
        {/* Header */}
        <div className="mb-6">
          <p className="text-sm text-[#676767]">Add an extra layer of security to your account</p>
        </div>

        {/* MFA Status Card */}
        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                mfaStatus.mfa_enabled 
                  ? 'bg-green-100 dark:bg-green-900/30' 
                  : 'bg-[#e8ede6] dark:bg-[#2a2a2a]'
              }`}>
                {mfaStatus.mfa_enabled ? (
                  <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Shield className="w-5 h-5 text-[#676767]" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#1c1a1c]">
                  Two-Factor Authentication is {mfaStatus.mfa_enabled ? 'Enabled' : 'Disabled'}
                </p>
                <p className="text-sm text-[#676767]">
                  {mfaStatus.mfa_enabled 
                    ? 'Your account is protected' 
                    : 'Your account is not protected'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Required Methods Section */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-[#1c1a1c] mb-2">Required Methods</h3>
          <p className="text-sm text-[#676767] mb-3">
            We require enabling both methods for maximum security
          </p>

          {/* Authenticator App Option */}
          <Card 
            className="bg-white/90 dark:bg-[#141414] border-0 shadow-sm mb-3 cursor-pointer hover:shadow-md transition-shadow"
            onClick={handleSetupAuthenticator}
            data-testid="authenticator-app-option"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#e8ede6] dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-[#757150]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#1c1a1c]">Authenticator App</p>
                    {mfaStatus.totp_enabled && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full">
                        Enabled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#676767]">
                    Use an app like Google Authenticator, Authy, or Microsoft Authenticator to generate verification codes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recovery Key Option */}
          <Card 
            className="bg-white/90 dark:bg-[#141414] border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={handleOpenRecoveryKey}
            data-testid="recovery-key-option"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#e8ede6] dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                  <Key className="w-5 h-5 text-[#757150]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#1c1a1c]">Recovery Key</p>
                    {mfaStatus.recovery_key_enabled && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full">
                        Enabled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#676767]">
                    Generate a recovery key for use when you can't access your other methods
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Why Enable 2FA Section */}
        <Card className="bg-white/70 dark:bg-[#1a1a1a] border-0">
          <CardContent className="p-4">
            <h4 className="font-semibold text-[#1c1a1c] mb-2">
              Why enable Two-Factor Authentication?
            </h4>
            <ul className="space-y-2 text-sm text-[#676767]">
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>Adds an extra layer of security to your account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>Protects against unauthorized access even if your password is compromised</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>Required for accessing sensitive features and data</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Return to Onboarding Button (shown when both MFA methods enabled) */}
        {mfaStatus.totp_enabled && mfaStatus.recovery_key_enabled && (
          <Button 
            className="w-full mt-4 bg-[#2C3E50] hover:bg-[#34495E] text-white h-12"
            onClick={() => navigate('/')}
            data-testid="return-to-onboarding-btn"
          >
            Return to Onboarding
          </Button>
        )}
      </div>

      {authenticatorModal}
      {recoveryKeyModal}
    </div>
  );
};

export default MFAPage;
